/**
 * Invite a member via the mediated Stytch invite endpoint.
 * Surfaces Stytch rejections verbatim — no local pre-validation.
 *
 * Presentational only — the live container is InviteMemberFormSection.tsx.
 */

import { Button } from '@localagent/ui/components/button';

export interface InviteMemberFormProps {
  email: string;
  onEmailChange: (email: string) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  error?: string | null;
  success?: string | null;
  disabled?: boolean;
}

export function InviteMemberForm({
  email,
  onEmailChange,
  onSubmit,
  isSubmitting = false,
  error = null,
  success = null,
  disabled = false,
}: InviteMemberFormProps) {
  return (
    <div className="rounded-lg bg-card p-4 space-y-3" data-testid="invite-member-form">
      <div>
        <h3 className="text-label font-medium">Invite member</h3>
        <p className="text-caption text-muted-foreground">
          Stytch delivers the invitation email, subject to the invite policy
          above.
        </p>
      </div>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <label className="space-y-1 text-body grow min-w-[12rem]">
          <span className="font-medium text-muted-foreground">Email</span>
          <input
            data-testid="invite-member-email"
            type="email"
            value={email}
            disabled={disabled || isSubmitting}
            onChange={(e) => onEmailChange(e.target.value)}
            className="block w-full rounded bg-muted px-2 py-1"
            placeholder="colleague@company.com"
            required
          />
        </label>
        <Button
          size="sm"
          type="submit"
          data-testid="invite-member-submit"
          disabled={disabled || isSubmitting || !email.trim()}
        >
          {isSubmitting ? 'Sending…' : 'Send invite'}
        </Button>
      </form>
      {error ? (
        <p className="text-body text-destructive" data-testid="invite-member-error">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="text-body text-success-text" data-testid="invite-member-success">
          {success}
        </p>
      ) : null}
      {disabled ? (
        <p
          className="text-body text-muted-foreground"
          data-testid="invite-member-disallowed"
        >
          Invitations are not available for your role.
        </p>
      ) : null}
    </div>
  );
}
